import {
  ChangeControlSeverity,
  useGetChangeControlStatusQuery,
} from 'src/generated/graphql';

/**
 * The active change-control window, when there is one. Every field is present,
 * so a returned window is always a complete, renderable banner.
 */
export interface ActiveChangeControlWindow {
  severity: ChangeControlSeverity.Restricted | ChangeControlSeverity.Blocked;
  /** Human-readable rule title from CCNS (e.g. the peak-freeze event name). */
  reason: string;
  /** Start of the window, epoch milliseconds. */
  windowStart: number;
  /** End of the window, epoch milliseconds. */
  windowEnd: number;
}

/**
 * Whether the current moment falls inside a change-control (peak-freeze) window.
 * Read wherever an edit surface or the app shell needs the window — the answer is a
 * global fact, identical for every route and caller, so Apollo's cache serves every
 * reader after the first for free.
 *
 * FAILS OPEN — `null` while in flight, on error, on a malformed payload, and on a
 * normal day. The banner is informational: the authoritative enforcement lives in
 * the backend submit path, so a missing banner can never let a submission through,
 * whereas a spurious banner would tell every user they are frozen when they are
 * not. Absence of calendar data must never render as a freeze.
 *
 * Uses `cache-first` (Apollo's default) deliberately: the value is read once at
 * shell mount and the resolver is uncached server-side, so a page refresh is what
 * re-evaluates `now ∈ window`. A second copy in Apollo's cache costs nothing and
 * spares navigations within the app from re-asking.
 */
export const useChangeControlStatus = (): ActiveChangeControlWindow | null => {
  const { data, error } = useGetChangeControlStatusQuery();

  if (error || !data) {
    return null;
  }

  const { severity, activeWindow } = data.getChangeControlStatus;

  // NONE is the normal-day answer. A non-NONE severity without a window is a
  // contract violation on the wire — the schema declares activeWindow's members
  // non-null, so a partial window is already dropped by the resolver — and the
  // fail-open rule says it renders as nothing rather than as a half-filled banner.
  if (severity === ChangeControlSeverity.None || !activeWindow) {
    return null;
  }

  return {
    severity,
    reason: activeWindow.reason,
    windowStart: activeWindow.windowStart,
    windowEnd: activeWindow.windowEnd,
  };
};
